// Kurerad statisk data för grundlagar och rättsprinciper. Dessa förändras
// sällan och förtjänar handskrivna, korrekta förklaringar snarare än AI-genererade.

export interface StaticLaw {
  slug: string;
  title: string;
  short: string;
  summary: string;
  official_url: string;
  sfs_nr?: string;
  /** Var i det länkade dokumentet principen/regeln uttrycks. Visas i en
   * modal före användaren skickas till riksdagen.se så hen inte tappar
   * bort sig i en lång lagtext. */
  source_location?: {
    /** Kort namn på källan, t.ex. "Brottsbalken" eller "Europakonventionen". */
    document: string;
    /** Exakt paragraf/artikel, t.ex. "1 kap. 1 §" eller "Artikel 6.2". */
    reference: string;
    /** Kort tips om hur man hittar rätt, t.ex. "Sök på sidan efter '1 §'". */
    hint?: string;
  };
}

export const GRUNDLAGAR: StaticLaw[] = [
  {
    slug: "regeringsformen",
    sfs_nr: "1974:152",
    title: "Regeringsformen",
    short: "Statsskickets grund – demokrati, rättigheter, myndighetsutövning.",
    summary:
      "Regeringsformen är Sveriges viktigaste grundlag och bestämmer hur landet ska styras. Den slår fast att all offentlig makt utgår från folket, att Sverige är en demokrati med parlamentarism och kommunal självstyre, och listar dina grundläggande fri- och rättigheter (yttrandefrihet, mötesfrihet, skydd mot kroppsligt ingrepp, egendomsskydd med mera). Här finns också reglerna för regeringen, riksdagen, domstolarna och myndigheterna.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/kungorelse-1974152-om-beslutad-ny-regeringsform_sfs-1974-152/",
  },
  {
    slug: "successionsordningen",
    sfs_nr: "1810:0926",
    title: "Successionsordningen",
    short: "Vem som ärver den svenska tronen.",
    summary:
      "Successionsordningen reglerar arvsrätten till den svenska tronen. Sedan 1980 gäller full kognatisk primogenitur, vilket betyder att äldsta barnet ärver oavsett kön. Kungen och den kungliga familjen måste också bekänna sig till evangelisk-luthersk tro.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/successionsordning-18100926_sfs-1810-0926/",
  },
  {
    slug: "tryckfrihetsforordningen",
    sfs_nr: "1949:105",
    title: "Tryckfrihetsförordningen",
    short: "Rätten att trycka och sprida skrifter – och offentlighetsprincipen.",
    summary:
      "Tryckfrihetsförordningen skyddar rätten att fritt uttrycka sig i tryckta skrifter (böcker, tidningar, flygblad). Den innehåller också offentlighetsprincipen — rätten att ta del av allmänna handlingar hos myndigheter — samt meddelarfrihet och källskydd som gör att den som lämnar uppgifter till journalister har rätt att vara anonym.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/tryckfrihetsforordning-1949105_sfs-1949-105/",
  },
  {
    slug: "yttrandefrihetsgrundlagen",
    sfs_nr: "1991:1469",
    title: "Yttrandefrihetsgrundlagen",
    short: "Motsvarigheten till tryckfriheten fast för radio, tv, film och webb.",
    summary:
      "Yttrandefrihetsgrundlagen ger samma grundläggande skydd som tryckfrihetsförordningen fast för moderna medier: radio, tv, film, video och tekniska upptagningar. Nyhetssajter och strömningstjänster med utgivningsbevis omfattas också. Även här gäller meddelarfrihet, censurförbud och en särskild rättegångsordning med jury.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/yttrandefrihetsgrundlag-19911469_sfs-1991-1469/",
  },
];

export const PRINCIPER: StaticLaw[] = [
  {
    slug: "legalitetsprincipen",
    title: "Legalitetsprincipen",
    short: "Inget brott utan lag, inget straff utan lag.",
    summary:
      "Legalitetsprincipen innebär att en handling bara kan vara brottslig om det uttryckligen står i en lag som gällde när handlingen begicks. Straff får bara utdömas med stöd av lag och lagen får inte tillämpas i strid med sin ordalydelse (analogiförbud). Principen finns i 1 kap. 1 § brottsbalken och i Europakonventionens artikel 7.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/brottsbalk-1962700_sfs-1962-700/",
    source_location: {
      document: "Brottsbalken (1962:700)",
      reference: "1 kap. 1 §",
      hint: "Sidan är lång — sök (Ctrl/⌘+F) efter \"1 §\" och läs första paragrafen under kapitel 1.",
    },
  },
  {
    slug: "proportionalitetsprincipen",
    title: "Proportionalitetsprincipen",
    short: "Myndighetens åtgärd får inte vara mer ingripande än nödvändigt.",
    summary:
      "Proportionalitetsprincipen kräver att en myndighetsåtgärd (t.ex. husrannsakan, hämtning, beslag) står i rimlig proportion till syftet. En polis får inte använda mer våld än vad situationen kräver, och skatteverket får inte kräva in mer information än vad som är nödvändigt.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/forvaltningslag-2017900_sfs-2017-900/",
    source_location: {
      document: "Förvaltningslagen (2017:900)",
      reference: "5 §",
      hint: "Sök efter \"5 §\" i lagtexten — där uttrycks proportionalitetskravet för myndigheter.",
    },
  },
  {
    slug: "oskuldspresumtionen",
    title: "Oskuldspresumtionen",
    short: "Alla är oskyldiga tills motsatsen bevisats.",
    summary:
      "Oskuldspresumtionen betyder att den som är misstänkt eller åtalad ska behandlas som oskyldig tills en domstol dömer i lagakraftvunnen dom. Åklagaren har hela bevisbördan och skulden ska bevisas 'utom rimligt tvivel'. Principen kommer från Europakonventionens artikel 6.2.",
    official_url: "https://www.echr.coe.int/documents/convention_swe.pdf",
    source_location: {
      document: "Europakonventionen (PDF)",
      reference: "Artikel 6.2",
      hint: "Öppnas som PDF. Bläddra till artikel 6, punkt 2 (\"Rätt till en rättvis rättegång\").",
    },
  },
  {
    slug: "objektivitetsprincipen",
    title: "Objektivitetsprincipen",
    short: "Myndigheter måste vara sakliga och opartiska.",
    summary:
      "Objektivitetsprincipen står i 1 kap. 9 § regeringsformen och innebär att domstolar och myndigheter ska iaktta saklighet och opartiskhet i sitt arbete. Polis och åklagare ska utreda även omständigheter som talar för den misstänktes oskuld.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/kungorelse-1974152-om-beslutad-ny-regeringsform_sfs-1974-152/",
    source_location: {
      document: "Regeringsformen (1974:152)",
      reference: "1 kap. 9 §",
      hint: "Sök efter \"9 §\" under kapitel 1 i regeringsformen.",
    },
  },
  {
    slug: "offentlighetsprincipen",
    title: "Offentlighetsprincipen",
    short: "Rätten att ta del av allmänna handlingar.",
    summary:
      "Offentlighetsprincipen ger var och en rätt att ta del av allmänna handlingar hos myndigheter, gratis och utan att behöva ange skäl. Endast om handlingen omfattas av sekretess enligt offentlighets- och sekretesslagen får den nekas. Principen är en av hörnstenarna i svensk demokrati.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/tryckfrihetsforordning-1949105_sfs-1949-105/",
    source_location: {
      document: "Tryckfrihetsförordningen (1949:105)",
      reference: "2 kap.",
      hint: "Hela 2 kap. handlar om allmänna handlingars offentlighet — börja där.",
    },
  },
  {
    slug: "likhetsprincipen",
    title: "Likhetsprincipen",
    short: "Lika fall ska behandlas lika.",
    summary:
      "Likhetsprincipen innebär att myndigheter måste behandla lika fall lika. Två personer i samma situation ska få samma beslut. Principen står i regeringsformen och förvaltningslagen och skyddar mot godtycke.",
    official_url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/forvaltningslag-2017900_sfs-2017-900/",
    source_location: {
      document: "Förvaltningslagen (2017:900)",
      reference: "5 §",
      hint: "Uttrycks tillsammans med objektivitetskravet i 5 § — sök efter \"5 §\".",
    },
  },
  {
    slug: "ne-bis-in-idem",
    title: "Ne bis in idem (dubbelbestraffningsförbud)",
    short: "Ingen får dömas två gånger för samma sak.",
    summary:
      "Ne bis in idem betyder 'inte två gånger för samma sak' och innebär att en person inte får åtalas eller straffas två gånger för samma gärning. Principen finns i Europakonventionens tilläggsprotokoll 7 och EU:s rättighetsstadga artikel 50.",
    official_url: "https://www.echr.coe.int/documents/convention_swe.pdf",
    source_location: {
      document: "Europakonventionen, tilläggsprotokoll 7 (PDF)",
      reference: "Artikel 4",
      hint: "Öppnas som PDF. Bläddra långt ner till tilläggsprotokoll 7, artikel 4.",
    },
  },
];